import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import PhoneInput from '../components/PhoneInput'
import BDAddressFields from '../components/BDAddressFields'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'

// Legacy const names kept for minimal diff — now mapped to the shared CSS design tokens
const BLUE   = 'var(--primary)'
const TEXT   = 'var(--text-primary)'
const MUTED  = 'var(--text-muted)'
const BORDER = 'var(--border)'
const BG     = 'var(--bg-muted)'
const WHITE  = '#FFFFFF'
const GREEN  = 'var(--success)'
const RED    = 'var(--error)'

const BD_COURIERS = ['Pathao','Steadfast','RedX','Sundarban','eCourier','Self delivery','Manual delivery','Customer pickup','Event / Fair pickup']

const FLAG_LEVEL_CONFIG = {
  watch:     { label: 'Watch',     color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  caution:   { label: 'Caution',   color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  high_risk: { label: 'High Risk', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
}
const INT_COURIERS = ['AusPost','Royal Mail','USPS','Canada Post','NZ Post','DHL','FedEx','UPS','Aramex','Singapore Post','Pos Malaysia','J&T Express','Ninja Van','Uber / rideshare','Manual delivery','Customer pickup','Event / Fair pickup','Friend / volunteer']
const PAYMENT_BY_CURRENCY = {
  BDT: ['Cash on delivery','bKash','Nagad','Rocket','Upay','Bank transfer','Cash','At counter'],
  USD: ['Cash on delivery','Cash','PayPal','Wise','Venmo','Cash App','Zelle','Bank transfer','At counter'],
  GBP: ['Cash on delivery','Cash','PayPal','Wise','Bank transfer','Faster Payments','At counter'],
  EUR: ['Cash on delivery','Cash','PayPal','Wise','SEPA transfer','Bank transfer','At counter'],
  AUD: ['Cash on delivery','Cash','PayID','Bank transfer','PayPal','Wise','At counter'],
  CAD: ['Cash on delivery','Cash','Interac e-Transfer','PayPal','Bank transfer','At counter'],
  DEFAULT: ['Cash on delivery','Cash','Bank transfer','PayPal','Wise','At counter'],
}
const PAYMENT_STATUSES = ['Unpaid COD','Paid in full','50% advance','Partial payment','Bank transfer']
const ORDER_SOURCES = ['Facebook inbox','Facebook page','Facebook comment','WhatsApp','Instagram','TikTok','Phone call','Walk-in','Website','Event / Fair','Other']

// Standardised buckets so CRM analytics (gender mix, age groups) stay clean —
// avoids free-text fragmentation like "COD" vs "Cash on delivery"
const GENDER_OPTIONS = ['Not specified', 'Female', 'Male', 'Other']
const AGE_GROUPS = ['Not specified', 'Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+']

// Fuzzy-match a parsed/free-text value to one of a fixed dropdown's options.
// Returns '' if nothing matches confidently, so analytics never gets a stray value.
function matchOption(value, options) {
  if (!value) return ''
  const v = value.toLowerCase().trim()
  const exact = options.find(o => o.toLowerCase() === v)
  if (exact) return exact
  if (options === GENDER_OPTIONS) {
    if (/^f(emale)?$/.test(v)) return 'Female'
    if (/^m(ale)?$/.test(v)) return 'Male'
    if (v.includes('other')) return 'Other'
    return ''
  }
  if (options === AGE_GROUPS) {
    const num = parseInt(v.match(/\d+/)?.[0], 10)
    if (isNaN(num)) return ''
    if (num < 18) return 'Under 18'
    if (num <= 24) return '18-24'
    if (num <= 34) return '25-34'
    if (num <= 44) return '35-44'
    if (num <= 54) return '45-54'
    if (num <= 64) return '55-64'
    return '65+'
  }
  return ''
}

const PASTE_FIELDS = [
  { label: 'Product code', key: 'product_code' },
  { label: 'Quantity',     key: 'quantity' },
  { label: 'Full name',    key: 'name' },
  { label: 'Phone',        key: 'phone' },
  { label: 'House',        key: 'house' },
  { label: 'Road',         key: 'road' },
  { label: 'Moholla',      key: 'moholla' },
  { label: 'Thana',        key: 'thana' },
  { label: 'Union',        key: 'union_name' },
  { label: 'Upazilla',     key: 'upazilla_text' },
  { label: 'District',     key: 'district' },
  { label: 'Division',     key: 'division' },
  { label: 'Country',      key: 'country' },
  { label: 'ZIP',          key: 'zip' },
  { label: 'Birthday',     key: 'birthday' },
  { label: 'Anniversary',  key: 'anniversary' },
  { label: 'Gender',       key: 'gender' },
  { label: 'Age group',    key: 'age_range' },
  { label: 'WhatsApp',     key: 'whatsapp' },
  { label: 'Email',        key: 'email' },
  { label: 'Facebook',     key: 'facebook_url' },
  { label: 'Instagram',    key: 'instagram_url' },
  { label: 'Notes',        key: 'notes' },
]

function parseReply(text) {
  const result = {}
  text.split('\n').forEach(line => {
    const colonIdx = line.indexOf(':')
    if (colonIdx < 0) return
    const rawLabel = line.slice(0, colonIdx).replace(/[^\w\s/]/gu, '').trim().toLowerCase()
    const value    = line.slice(colonIdx + 1).trim()
    if (!value || ['n/a','na','-',''].includes(value.toLowerCase())) return
    for (const f of PASTE_FIELDS) {
      const fClean = f.label.toLowerCase()
      const fWords = fClean.split(/[\s/]+/)
      if (fWords.some(w => w.length > 2 && rawLabel.includes(w)) ||
          rawLabel.split(/[\s/]+/).some(w => w.length > 2 && fClean.includes(w))) {
        result[f.key] = value; break
      }
    }
  })
  return result
}

// ── Shared UI ─────────────────────────────────────────────────────────────
function Field({ label, optional, children }) {
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      {children}
    </div>
  )
}

const baseInp = { width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }

function Inp({ value, onChange, placeholder, type = 'text', min, style: extra }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} min={min}
      style={{ ...baseInp, ...extra }}
      onFocus={e => e.target.style.borderColor = BLUE}
      onBlur={e => e.target.style.borderColor = BORDER} />
  )
}

function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ ...baseInp }}
      onFocus={e => e.target.style.borderColor = BLUE}
      onBlur={e => e.target.style.borderColor = BORDER}>
      {children}
    </select>
  )
}

function Card({ title, subtitle, children, accent }) {
  return (
    <div className="pc-fade-up" style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, boxShadow: 'var(--shadow-sm)', overflow: 'visible', marginBottom: '16px' }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG, borderLeft: accent ? '4px solid ' + BLUE : 'none', borderRadius: accent ? '14px 14px 0 0' : '14px 14px 0 0' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT, fontFamily: 'var(--font-ui)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '3px' }}>{subtitle}</div>}
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  )
}

function StepDot({ n, label, active, done, onClick }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
      <button onClick={done ? onClick : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: done ? 'pointer' : 'default', background: 'none', border: 'none', padding: 0 }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, background: done ? GREEN : active ? BLUE : BORDER, color: done || active ? WHITE : MUTED }}>
          {done ? '✓' : n}
        </div>
        <span className="ao-step-label" style={{ fontSize: '12.5px', fontWeight: 600, color: active ? 'var(--text-primary)' : done ? 'var(--success)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{label}</span>
      </button>
    </div>
  )
}

function Toggle({ enabled, onToggle, label, summary }) {
  return (
    <div style={{ borderRadius: '10px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '12px' }}>
      <button type="button" onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: enabled ? 'var(--primary-light)' : BG, border: 'none', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: '2px solid', borderColor: enabled ? BLUE : BORDER, background: enabled ? BLUE : WHITE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {enabled && <span style={{ color: WHITE, fontSize: '9px', fontWeight: 700 }}>✓</span>}
          </div>
          <span style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{label}</span>
        </div>
        {enabled && summary && <span style={{ fontSize: '12px', fontWeight: 700, color: BLUE }}>{summary}</span>}
      </button>
    </div>
  )
}

// ── Cart line calculator ───────────────────────────────────────────────────
function calcLine(item) {
  const base     = Number(item.quantity || 0) * Number(item.unit_price || 0)
  const discAmt  = item.line_discount_percent ? Math.round(base * Number(item.line_discount_percent) / 100) : 0
  const afterDisc = base - discAmt
  const vatAmt   = item.line_vat_percent ? Math.round(afterDisc * Number(item.line_vat_percent) / 100) : 0
  return { base, discAmt, afterDisc, vatAmt, subtotal: afterDisc + vatAmt }
}

// ── Cart item row ─────────────────────────────────────────────────────────
function CartItemRow({ item, index, onUpdate, onRemove, sym, products, t }) {
  const [variantList, setVariantList]     = useState([])
  const [loadingVariants, setLoadingVariants] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  const [showDropdown, setShowDropdown]   = useState(false)
  const [showSearch, setShowSearch]       = useState(!item.product_id)
  const searchRef = useRef(null)

  // Load variants when product changes
  useEffect(() => {
    if (!item.product_id) { setVariantList([]); return }
    setLoadingVariants(true)
    supabase.from('product_variants')
      .select('id, variant_name, variant_code, stock, sell_price, weight_kg')
      .eq('product_id', item.product_id).order('variant_name')
      .then(({ data }) => {
        const vList = data || []
        setVariantList(vList)
        setLoadingVariants(false)
        // Auto-select if only one variant
        if (vList.length === 1 && !item.variant_id) {
          onUpdate(index, {
            variant_id: vList[0].id,
            variant_name: vList[0].variant_name,
            unit_price: vList[0].sell_price || item.unit_price,
            weight_kg: vList[0].weight_kg || item.weight_kg || null,
          })
        }
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.product_id])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = products.filter(p =>
    !productSearch ||
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.product_code?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.color?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.product_type?.toLowerCase().includes(productSearch.toLowerCase())
  )

  const selectProduct = (p) => {
    onUpdate(index, {
      product_id: p.id, product_code: p.product_code,
      product_name: p.name, product_color: p.color, product_type_label: p.product_type,
      product_image: p.image_url || '',
      unit_price: p.sell_price || 0, weight_kg: p.weight_kg || null, variant_id: '', variant_name: '',
    })
    setShowSearch(false)
    setShowDropdown(false)
    setProductSearch('')
  }

  const calc = calcLine(item)
  const needsVariant = variantList.length > 0 && !item.variant_id

  return (
    <div className="pc-card-hover" style={{ border: '1px solid ' + (needsVariant ? '#FDE68A' : BORDER), borderRadius: '12px', padding: '14px', marginBottom: '10px', background: WHITE, position: 'relative' }}>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Product {index + 1}
          {needsVariant && <span style={{ color: '#D97706', marginLeft: '8px', fontWeight: 600, textTransform: 'none', fontSize: '11px' }}>⚠ select a variant</span>}
        </div>
        {index > 0 && (
          <button onClick={() => onRemove(index)}
            style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#FEF2F2', border: '1px solid #FECACA', color: RED, fontSize: '14px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, flexShrink: 0 }}>
            ×
          </button>
        )}
      </div>

      {/* ── Product search ── */}
      {showSearch || !item.product_id ? (
        <div style={{ marginBottom: '10px', position: 'relative' }} ref={searchRef}>
          <div style={{ position: 'relative' }}>
            <input value={productSearch}
              onChange={e => { setProductSearch(e.target.value); setShowDropdown(true) }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Type product name, colour or code to search..."
              style={{ ...baseInp, paddingLeft: '34px' }}
              autoFocus />
            <div style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </div>
          </div>
          {showDropdown && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', border: '1px solid ' + BORDER, borderRadius: '10px', background: WHITE, boxShadow: 'var(--shadow-md)', zIndex: 100, maxHeight: '260px', overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: MUTED }}>No products found</div>
              ) : filtered.slice(0, 12).map(p => (
                <div key={p.id}
                  onMouseDown={() => selectProduct(p)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid ' + BORDER, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Name + badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '3px' }}>
                      <span style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT }}>{p.name}</span>
                      {p.color && (
                        <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: '#6D28D9', flexShrink: 0 }}>
                          {p.color}
                        </span>
                      )}
                      {p.product_type && (
                        <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: BG, color: MUTED, flexShrink: 0 }}>
                          {p.product_type}
                        </span>
                      )}
                    </div>
                    {/* Code + stock */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '11px', color: BLUE, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{p.product_code}</span>
                      <span style={{ fontSize: '11px', color: p.stock <= 5 ? RED : GREEN }}>{p.stock} in stock</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{sym}{Number(p.sell_price || 0).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* ── Selected product display ── */
        <div style={{ padding: '10px 13px', borderRadius: '9px', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', marginBottom: '10px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
          {/* Thumbnail */}
          <div style={{ width: '44px', height: '44px', borderRadius: '8px', flexShrink: 0, overflow: 'hidden', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {item.product_image
              ? <img src={item.product_image} alt={item.product_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="1.5" strokeLinecap="round"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '3px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{item.product_name}</span>
              {item.product_color && (
                <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: '#6D28D9' }}>{item.product_color}</span>
              )}
              {item.product_type_label && (
                <span style={{ fontSize: '10.5px', padding: '1px 7px', borderRadius: 'var(--radius-full)', background: BG, color: MUTED }}>{item.product_type_label}</span>
              )}
            </div>
            <span style={{ fontSize: '11.5px', color: BLUE, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{item.product_code}</span>
          </div>
          <button onClick={() => { onUpdate(index, { product_id: '', product_code: '', product_name: '', product_color: '', product_type_label: '', product_image: '', variant_id: '', variant_name: '' }); setShowSearch(true); setVariantList([]) }}
            style={{ fontSize: '11.5px', fontWeight: 600, padding: '5px 10px', borderRadius: '7px', border: '1px solid ' + BORDER, background: WHITE, color: MUTED, cursor: 'pointer', flexShrink: 0 }}>
            Change
          </button>
        </div>
      )}

      {/* ── Variant picker ── */}
      {item.product_id && (
        <div style={{ marginBottom: '12px' }}>
          {loadingVariants ? (
            <div style={{ fontSize: '12px', color: MUTED, padding: '6px 0' }}>Loading variants...</div>
          ) : variantList.length === 0 ? (
            <div style={{ fontSize: '11.5px', color: GREEN, padding: '4px 0' }}>✓ No size/weight variants — one version only</div>
          ) : (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Select variant * — {variantList.length} option{variantList.length !== 1 ? 's' : ''} (size / weight / pack)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                {variantList.map(v => {
                  const isSelected = item.variant_id === v.id
                  const isLow = v.stock <= 5
                  const isOut = v.stock === 0
                  return (
                    <button key={v.id}
                      onClick={() => !isOut && onUpdate(index, { variant_id: v.id, variant_name: v.variant_name, unit_price: v.sell_price || item.unit_price, weight_kg: v.weight_kg || item.weight_kg || null })}
                      disabled={isOut}
                      style={{
                        padding: '8px 13px', borderRadius: '9px', cursor: isOut ? 'not-allowed' : 'pointer',
                        border: '2px solid', borderColor: isSelected ? BLUE : isLow ? '#FDE68A' : BORDER,
                        background: isSelected ? 'var(--primary-light)' : isOut ? '#F9FAFB' : WHITE,
                        opacity: isOut ? 0.5 : 1, textAlign: 'left', transition: 'all 0.1s',
                      }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 700, color: isSelected ? BLUE : isOut ? MUTED : TEXT }}>
                        {v.variant_name}
                        {isSelected && <span style={{ marginLeft: '5px', fontSize: '11px' }}>✓</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '3px', alignItems: 'center' }}>
                        {v.sell_price && v.sell_price !== item.unit_price && (
                          <span style={{ fontSize: '11px', fontWeight: 700, color: isSelected ? BLUE : TEXT }}>{sym}{Number(v.sell_price).toLocaleString()}</span>
                        )}
                        <span style={{ fontSize: '10.5px', fontWeight: 600, color: isOut ? RED : isLow ? '#D97706' : GREEN }}>
                          {isOut ? t("add_order.out_of_stock") : isLow ? v.stock + ' left ⚠' : v.stock + ' left'}
                        </span>
                        {v.variant_code && (
                          <span style={{ fontSize: '9.5px', color: MUTED, fontFamily: 'JetBrains Mono, monospace' }}>{v.variant_code}</span>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Qty + Price ── */}
      <div className="ao-grid-2" style={{ marginBottom: '10px' }}>
        <Field label={t("add_order.quantity")}>
          <Inp type="number" value={item.quantity} onChange={v => onUpdate(index, { quantity: v })} min="1" placeholder="1" />
        </Field>
        <Field label={t("add_order.unit_price") + ` (${sym})`}>
          <Inp type="number" value={item.unit_price} onChange={v => onUpdate(index, { unit_price: v })} placeholder="0" />
        </Field>
      </div>

      {/* ── Per-line discount + VAT ── */}
      <div className="ao-grid-2" style={{ marginBottom: '10px' }}>
        <Field label="Line discount %" optional>
          <Inp type="number" value={item.line_discount_percent || ''} onChange={v => onUpdate(index, { line_discount_percent: v })} placeholder="e.g. 10" />
        </Field>
        <Field label="Line VAT / GST %" optional>
          <Inp type="number" value={item.line_vat_percent || ''} onChange={v => onUpdate(index, { line_vat_percent: v })} placeholder="e.g. 15" />
        </Field>
      </div>

      {/* ── Line subtotal ── */}
      <div style={{ padding: '9px 12px', borderRadius: '9px', background: BG, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
        <div style={{ fontSize: '11.5px', color: MUTED }}>
          {item.quantity} × {sym}{Number(item.unit_price || 0).toLocaleString()}
          {calc.discAmt > 0 && <span style={{ color: RED }}> &#8722; {sym}{calc.discAmt.toLocaleString()} {t("add_order.disc")}</span>}
          {calc.vatAmt > 0 && <span> + {sym}{calc.vatAmt.toLocaleString()} VAT</span>}
        </div>
        <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>
          {sym}{calc.subtotal.toLocaleString()}
        </div>
      </div>
    </div>
  )
}


// ── Parsed fields summary (shown in Step 2) ───────────────────────────────
function ParsedSummary({ parsedData }) {
  const [open, setOpen] = useState(true)
  const entries = Object.entries(parsedData)
  if (entries.length === 0) return null
  return (
    <div style={{ borderRadius: '10px', border: '1px solid var(--primary-border)', overflow: 'hidden', marginBottom: '16px' }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--primary-light)', border: 'none', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: BLUE }}>✓ {entries.length} fields extracted from customer reply</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11.5px', color: BLUE }}>{open ? 'Hide' : 'Show'}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>
      {open && (
        <div style={{ padding: '12px 14px', background: WHITE }}>
          <div style={{ fontSize: '11.5px', color: MUTED, marginBottom: '10px', lineHeight: 1.6 }}>
            These values have been auto-filled into the form fields below. Review each one carefully — the customer may have made typos.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
            {entries.map(([key, value]) => {
              const field = PASTE_FIELDS.find(f => f.key === key)
              const label = field ? field.label : key
              return (
                <div key={key} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '4px 0', borderBottom: '1px solid ' + BORDER }}>
                  <span style={{ fontSize: '11px', color: MUTED, flexShrink: 0, width: '80px', paddingTop: '1px' }}>{label}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT, wordBreak: 'break-word' }}>{value}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Flat icon set (20x20 viewBox) — consistent with CRM rebuild ───────────
const ICONS = {
  check:      'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  warning:    'M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.63-1.514 2.63H3.72c-1.345 0-2.187-1.463-1.514-2.63L8.485 2.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 7a1 1 0 100 2 1 1 0 000-2z',
  close:      'M5 5l10 10M15 5L5 15',
  arrowLeft:  'M9.707 4.293a1 1 0 010 1.414L7.414 8H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z',
  arrowRight: 'M10.293 15.707a1 1 0 010-1.414L12.586 12H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z',
  hourglass:  'M5 2a1 1 0 000 2v1.5a4 4 0 001.5 3.13L8 10l-1.5 1.37A4 4 0 005 14.5V16a1 1 0 100 2h10a1 1 0 100-2v-1.5a4 4 0 00-1.5-3.13L12 10l1.5-1.37A4 4 0 0015 5.5V4a1 1 0 100-2H5zm2 2h6v1.5a2 2 0 01-.75 1.565L10 8.5 7.75 7.065A2 2 0 017 5.5V4z',
  bulb:       'M10 2a6 6 0 00-3.5 10.9V14a1.5 1.5 0 001.5 1.5h4A1.5 1.5 0 0013.5 14v-1.1A6 6 0 0010 2zM8.5 17.5a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3z',
  chevronDown:'M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z',
  gift:       'M5 8a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4zM3 9a1 1 0 011-1h12a1 1 0 011 1v1a2 2 0 01-1 1.732V16a1 1 0 01-1 1H5a1 1 0 01-1-1v-4.268A2 2 0 013 10V9z',
  user:       'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z',
  mail:       'M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884zM18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z',
  chat:       'M2 10c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9.06 9.06 0 01-2.857-.45c-.832.74-1.917 1.45-2.943 1.45a.6.6 0 01-.55-.84c.31-.71.51-1.49.557-2.272A6.96 6.96 0 012 10z',
  external:   'M11 3a1 1 0 100 2h2.586L7.293 11.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5zM5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-2a1 1 0 10-2 0v2H5V7h2a1 1 0 100-2H5z',
  plus:       'M10 2a1 1 0 011 1v6h6a1 1 0 110 2h-6v6a1 1 0 11-2 0v-6H3a1 1 0 110-2h6V3a1 1 0 011-1z',
  flag:       'M3 3a1 1 0 00-1 1v13a1 1 0 102 0v-3.382l1.447-.724A4 4 0 018.764 13l.894.447a4 4 0 003.788 0L17 11.882V4.118l-3.553 1.776a2 2 0 01-1.789 0L9.764 4.67a4 4 0 00-3.528 0L4 5.618V4a1 1 0 00-1-1z',
  camera:     'M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-.828-.828A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586l-.828.828A1 1 0 015.879 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z',
}
function FlatIcon({ name, size = 14, color = 'currentColor', style }) {
  const d = ICONS[name]
  if (!d) return null
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color} style={{ flexShrink: 0, ...style }}>
      <path fillRule="evenodd" d={d} clipRule="evenodd" />
    </svg>
  )
}

// ── Collapsible "more info" section ────────────────────────────────────────
function CollapsibleSection({ title, hint, badge, open, onToggle, children }) {
  return (
    <div style={{ borderRadius: '10px', border: '1px solid var(--border)', overflow: 'hidden' }}>
      <button type="button" onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg-muted)', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <FlatIcon name="user" size={15} color="var(--text-muted)" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{title}</div>
            {hint && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{hint}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          {badge}
          <FlatIcon name="chevronDown" size={12} color="var(--text-muted)" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
        </div>
      </button>
      {open && <div style={{ padding: '14px', background: 'var(--bg-card)', display: 'flex', flexDirection: 'column', gap: '12px' }}>{children}</div>}
    </div>
  )
}

// ── MAIN ──────────────────────────────────────────────────────────────────
function AddOrder() {
  const navigate   = useNavigate()
  const location   = useLocation()
  const { seller } = useAuth()
  const { showToast } = useToast()

  const { t } = useTranslation()
  const isInt    = seller?.seller_mode === 'international'
  const currency = seller?.seller_currency || 'BDT'
  const sym      = getCurrencySymbol(currency)
  const isPreorder = new URLSearchParams(location.search).get('preorder') === 'true'

  const defaultCourier = isInt ? INT_COURIERS[0] : BD_COURIERS[0]
  const defaultPayment = (PAYMENT_BY_CURRENCY[currency] || PAYMENT_BY_CURRENCY.DEFAULT)[0]

  // ── State ─────────────────────────────────────────────────────────────
  const [step, setStep]         = useState(1)
  const [saving, setSaving]     = useState(false)
  const [products, setProducts] = useState([])
  const [events, setEvents]     = useState([])
  const [quickTab, setQuickTab] = useState('copy')
  const [generatingLink, setGeneratingLink] = useState(false)

  // Cart — array of line items
  const emptyLine = () => ({ product_id: '', product_code: '', product_name: '', product_color: '', product_type_label: '', product_image: '', variant_id: '', variant_name: '', quantity: 1, unit_price: '', weight_kg: null, line_discount_percent: '', line_vat_percent: '' })
  const [cart, setCart] = useState([emptyLine()])

  // Camera
  const videoRef  = useRef(null)
  const streamRef = useRef(null)
  const rafRef    = useRef(null)
  const [cameraOpen, setCameraOpen]   = useState(false)
  const [cameraError, setCameraError] = useState('')
  const [scanning, setScanning]       = useState(false)
  const [activeScanIndex, setActiveScanIndex] = useState(0)

  // Paste
  const [pasteText, setPasteText]   = useState('')
  const [parsedData, setParsedData] = useState(null)

  // Phone lookup
  const [searchPhone, setSearchPhone]             = useState('')
  const [searchCountryCode, setSearchCountryCode] = useState(isInt ? '+61' : '+880')
  const [existingCustomer, setExistingCustomer]   = useState(null)
  const [multiMatches, setMultiMatches]           = useState([])
  const [lookingUp, setLookingUp]                 = useState(false)

  // Customer profile (optional) section
  const [showProfileSection, setShowProfileSection] = useState(false)

  // Order-level discount / tax
  const [orderDiscountEnabled, setOrderDiscountEnabled] = useState(false)
  const [orderDiscountPercent, setOrderDiscountPercent] = useState('')
  const [orderDiscountNote, setOrderDiscountNote]       = useState('')
  const [orderTaxEnabled, setOrderTaxEnabled]           = useState(false)
  const [orderTaxType, setOrderTaxType]                 = useState('VAT')
  const [orderTaxPercent, setOrderTaxPercent]           = useState('')
  const [orderTaxChallan, setOrderTaxChallan]           = useState('')
  const [customCourier, setCustomCourier]               = useState('')
  const [flagCheck, setFlagCheck]                       = useState(null)
  const [showManualDelivery, setShowManualDelivery]     = useState(false)

  const [customer, setCustomer] = useState({
    name: '', country_code: isInt ? '+61' : '+880', phone: '',
    house: '', road: '', moholla: '', extra: '',
    thana: '', thana_id: '', district: '', district_id: '',
    division: '', division_id: '', union_name: '', union_id: '',
    zip: '', country: isInt ? '' : 'Bangladesh',
    metro_thana: '', metro_thana_id: '', neighbourhood: '', neighbourhood_id: '',
    // Optional customer profile — used by CRM for greetings, segmentation & quick contact
    whatsapp: '', email: '', facebook_url: '', instagram_url: '',
    gender: '', age_range: '', birthday: '', anniversary: '',
  })

  const [order, setOrder] = useState({
    delivery_charge: isInt ? 0 : 60,
    courier: defaultCourier, payment_method: defaultPayment,
    payment_status: 'Unpaid COD', amount_paid: 0,
    order_source: 'Facebook inbox', notes: '',
    taken_by: '', tracking_number: '', tracking_url: '',
    expected_delivery: '', event_id: '',
    delivery_person_name: '', delivery_person_phone: '',
  })

  const setC = (key, val) => setCustomer(c => ({ ...c, [key]: val }))
  const setO = (key, val) => setOrder(o => ({ ...o, [key]: val }))

  // Profile-field completeness — drives the collapsed/expanded state and the "what's missing" nudge
  const PROFILE_FIELDS = ['birthday', 'anniversary', 'gender', 'age_range', 'whatsapp', 'email', 'facebook_url', 'instagram_url']
  const filledProfileFields = PROFILE_FIELDS.filter(k => customer[k] && customer[k] !== 'Not specified')
  const profileComplete = filledProfileFields.length === PROFILE_FIELDS.length

  // ── Cart helpers ──────────────────────────────────────────────────────
  const updateCartItem = (index, changes) => setCart(c => c.map((item, i) => i === index ? { ...item, ...changes } : item))
  const removeCartItem = (index) => setCart(c => c.filter((_, i) => i !== index))
  const addCartItem    = () => setCart(c => [...c, emptyLine()])

  // ── Calculations ──────────────────────────────────────────────────────
  const lineCalcs      = cart.map(calcLine)
  const itemsSubtotal  = lineCalcs.reduce((s, c) => s + c.subtotal, 0)
  const orderDiscAmt   = orderDiscountEnabled && orderDiscountPercent ? Math.round(itemsSubtotal * Number(orderDiscountPercent) / 100) : 0
  const afterOrderDisc = itemsSubtotal - orderDiscAmt
  const orderTaxAmt    = orderTaxEnabled && orderTaxPercent ? Math.round(afterOrderDisc * Number(orderTaxPercent) / 100) : 0
  const grandTotal     = afterOrderDisc + orderTaxAmt + Number(order.delivery_charge || 0)
  const amountDue      = grandTotal - Number(order.amount_paid || 0)

  // ── Load data ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (seller?.id) { fetchProducts(); fetchEvents() }
    return () => stopCamera()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller])

  const fetchProducts = async () => {
    const { data } = await supabase.from('products')
      .select('id, name, product_code, sell_price, stock, product_type, color, image_url, weight_kg')
      .eq('seller_id', seller.id).eq('status', 'active').order('is_pinned', { ascending: false })
    setProducts(data || [])
  }

  const fetchEvents = async () => {
    const { data } = await supabase.from('seller_events')
      .select('id, name, start_date, status').eq('seller_id', seller.id)
      .in('status', ['upcoming', 'ongoing']).order('start_date', { ascending: true })
    setEvents(data || [])
  }

  // ── Camera ────────────────────────────────────────────────────────────
  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop())
    streamRef.current = null
    setCameraOpen(false)
    setScanning(false)
  }

  const openCamera = async (lineIndex) => {
    setActiveScanIndex(lineIndex)
    setCameraError('')
    setCameraOpen(true)
    setScanning(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } })
      streamRef.current = stream
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play() }
      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'qr_code', 'upc_a', 'upc_e'] })
        const tick = async () => {
          if (!streamRef.current) return
          try {
            const barcodes = await detector.detect(videoRef.current)
            if (barcodes.length > 0) {
              const code = barcodes[0].rawValue
              stopCamera()
              const found = products.find(p => p.product_code?.toLowerCase() === code.toLowerCase())
              if (found) {
                updateCartItem(activeScanIndex, { product_id: found.id, product_code: found.product_code, product_name: found.name, unit_price: found.sell_price || 0, variant_id: '', variant_name: '' })
                showToast(t("add_order.scanned") + " " + found.name)
              } else {
                showToast(t("add_order.scanned_not_found"), "error")
              }
              return
            }
          } catch {}
          rafRef.current = requestAnimationFrame(tick)
        }
        videoRef.current.addEventListener('playing', () => { rafRef.current = requestAnimationFrame(tick) }, { once: true })
      } else {
        setCameraError(t("add_order.camera_error_no_support"))
        setScanning(false)
      }
    } catch {
      setCameraError(t("add_order.camera_error_denied"))
      setCameraOpen(false)
      setScanning(false)
    }
  }

  // ── Phone lookup ──────────────────────────────────────────────────────
  const normalizePhone = (code, number) => {
    const clean = number.trim().replace(/\s+/g, '').replace(/-/g, '')
    if (clean.startsWith('+')) return clean
    return code + clean
  }

  // Apply a chosen/found customer record into the form
  const applyFoundCustomer = (found) => {
    setExistingCustomer(found)
    setMultiMatches([])
    setCustomer({
      name: found.name || '', country_code: found.country_code || searchCountryCode,
      phone: found.phone || '', house: found.house || '', road: found.road || '',
      moholla: found.moholla || '', thana: found.thana || '', thana_id: found.thana_id || '',
      district: found.district || '', district_id: found.district_id || '',
      division: found.division || '', division_id: found.division_id || '',
      union_name: found.union_name || '', union_id: found.union_id || '',
      zip: found.zip || '', country: found.country || '', extra: found.extra || '',
      metro_thana: found.metro_thana || '', metro_thana_id: found.metro_thana_id || '',
      neighbourhood: found.neighbourhood || '', neighbourhood_id: found.neighbourhood_id || '',
      // Profile fields — pulled straight from their CRM record so sellers never re-ask
      whatsapp: found.whatsapp || '', email: found.email || '',
      facebook_url: found.facebook_url || '', instagram_url: found.instagram_url || '',
      gender: found.gender || '', age_range: found.age_range || '',
      birthday: found.birthday || '', anniversary: found.anniversary || '',
    })
    // Auto-expand the profile section only if something's still missing — gives a quick "fill the gap" nudge
    const missing = ['birthday','anniversary','gender','age_range','whatsapp','email','facebook_url','instagram_url'].filter(k => !found[k])
    setShowProfileSection(missing.length > 0 && missing.length < 8)
    showToast(t("add_order.returning_toast") + " — " + found.name)
  }

  // Start a brand-new customer record for the looked-up phone
  const startNewCustomer = (full) => {
    setExistingCustomer(null)
    setMultiMatches([])
    setCustomer(prev => ({ ...prev, country_code: searchCountryCode, phone: full }))
    setShowProfileSection(false)
    showToast(t("add_order.new_customer_toast"), "info")
  }

  const lookupPhone = async () => {
    if (!searchPhone || searchPhone.length < 5) return
    setLookingUp(true)
    const full = normalizePhone(searchCountryCode, searchPhone)
    const vars2 = [full, searchPhone.trim()]
    if (full.startsWith('+880')) vars2.push('0' + full.slice(4))

    // Phone is a lookup aid, not a hard unique key — different people can share a number,
    // so collect ALL matches and let the seller pick if there's more than one.
    let matches = []
    for (const v of [...new Set(vars2)]) {
      const { data } = await supabase.from('customers').select('*').eq('phone', v).eq('seller_id', seller.id)
      if (data && data.length) matches.push(...data)
    }
    const uniqueMatches = Array.from(new Map(matches.map(m => [m.id, m])).values())

    if (uniqueMatches.length === 1) {
      applyFoundCustomer(uniqueMatches[0])
    } else if (uniqueMatches.length > 1) {
      setMultiMatches(uniqueMatches)
      setExistingCustomer(null)
    } else {
      startNewCustomer(full)
    }

    // Check if this phone is flagged
    try {
      const { data: pFlag } = await supabase.from('customer_flags').select('*').eq('flagged_by_seller_id', seller.id).eq('phone', full).eq('is_active', true).maybeSingle()
      const { data: cFlag } = await supabase.from('community_flags').select('*').eq('phone', full).maybeSingle()
      setFlagCheck((pFlag || cFlag) ? { privateFlag: pFlag || null, communityFlag: cFlag || null } : null)
    } catch { setFlagCheck(null) }

    setLookingUp(false)
  }

  // ── Paste parser ──────────────────────────────────────────────────────
  const handleParse = useCallback(() => {
    if (!pasteText.trim()) return
    const parsed = parseReply(pasteText)
    setParsedData(parsed)
    const customerKeys = ['name','phone','house','road','moholla','thana','union_name','district','division','country','zip','birthday','anniversary','whatsapp','email','facebook_url','instagram_url']
    customerKeys.forEach(k => { if (parsed[k]) setC(k, parsed[k]) })
    // Gender / age group go through fuzzy-matching so only clean dropdown values ever land in customer state
    if (parsed.gender) { const g = matchOption(parsed.gender, GENDER_OPTIONS); if (g) setC('gender', g) }
    if (parsed.age_range) { const a = matchOption(parsed.age_range, AGE_GROUPS); if (a) setC('age_range', a) }
    if (parsed.notes) setO('notes', parsed.notes)
    if (parsed.birthday || parsed.anniversary || parsed.gender || parsed.age_range || parsed.whatsapp || parsed.email) setShowProfileSection(true)
    showToast(t("add_order.parsed_toast") + " — " + Object.keys(parsed).length + " " + t("add_order.fields"))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteText])

  // ── Quick tools ───────────────────────────────────────────────────────
  const handleCopyMessage = () => {
    const shopName = seller?.shop_name || 'our shop'
    const msg = `Hi! To place your order with ${shopName}, please fill and send back:\n\nFull name:\nPhone:\nHouse / Flat number:\nRoad / Street / Village:\nMoholla / Para / Area:\nThana:\nUnion: (if applicable)\nUpazilla: (if applicable)\nDistrict:\nDivision:\nCountry:\nZIP: (optional)\nNotes: (optional)\n\nFill what applies. Write N/A for fields that don't apply.\n\nPowered by PageConnect`
    navigator.clipboard.writeText(msg)
    showToast(t("add_order.copied_template"))
  }

  const handleShareLink = async () => {
    setGeneratingLink(true)
    try {
      const token = Math.random().toString(36).slice(2) + Date.now().toString(36)
      const expiresAt = new Date(Date.now() + 7 * 60 * 1000).toISOString()
      const { error } = await supabase.from('customer_order_requests').insert({ seller_id: seller.id, token, expires_at: expiresAt, status: 'pending', product_code: null })
      if (error) { showToast(t("add_order.link_error"), "error"); return }
      await navigator.clipboard.writeText(`https://pageconnect-app.vercel.app/order-form/${token}`)
      showToast(t("add_order.link_copied"))
    } catch {
      showToast(t("add_order.link_copy_error"), "error")
    } finally {
      setGeneratingLink(false)
    }
  }

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!customer.name || !customer.phone) { showToast(t("add_order.error_name_phone"), "error"); return }
    const validLines = cart.filter(l => l.product_id)
    if (validLines.length === 0) { showToast(t("add_order.error_no_product"), "error"); return }
    // Check each product line — if it has variants loaded in cart state, variant must be selected
    const productIds = validLines.map(l => l.product_id).filter(Boolean)
    const { data: variantCounts } = await supabase
      .from('product_variants').select('product_id')
      .in('product_id', productIds)
    const productsWithVariants = new Set((variantCounts || []).map(v => v.product_id))
    const missingVariant = validLines.find(l => productsWithVariants.has(l.product_id) && !l.variant_id)
    if (missingVariant) {
      showToast(t("add_order.error_select_variant") + " " + (missingVariant.product_name || "a product"), "error")
      return
    }
    setSaving(true)

    try {
      const fullPhone = normalizePhone(customer.country_code, customer.phone)
      let customerId = existingCustomer?.id

      // Profile fields shared between insert & update — only written when filled,
      // so we never overwrite existing CRM data with blanks
      const profilePatch = {}
      ;['whatsapp','email','facebook_url','instagram_url','gender','age_range','birthday','anniversary'].forEach(k => {
        if (customer[k]) profilePatch[k] = customer[k]
      })

      if (!customerId) {
        const { data: nc } = await supabase.from('customers').insert({
          seller_id: seller.id, name: customer.name, phone: fullPhone,
          country_code: customer.country_code, house: customer.house, road: customer.road,
          moholla: customer.moholla, thana: customer.thana, thana_id: customer.thana_id || null,
          district: customer.district, district_id: customer.district_id || null,
          division: customer.division, division_id: customer.division_id || null,
          union_name: customer.union_name || null, union_id: customer.union_id || null,
          zip: customer.zip, country: customer.country, extra: customer.extra || null,
          metro_thana: customer.metro_thana || null, metro_thana_id: customer.metro_thana_id || null,
          neighbourhood: customer.neighbourhood || null, neighbourhood_id: customer.neighbourhood_id || null,
          ...profilePatch,
        }).select().single()
        customerId = nc?.id
      } else {
        await supabase.from('customers').update({
          name: customer.name, house: customer.house, road: customer.road,
          moholla: customer.moholla, thana: customer.thana, thana_id: customer.thana_id || null,
          district: customer.district, district_id: customer.district_id || null,
          division: customer.division, division_id: customer.division_id || null,
          union_name: customer.union_name || null, union_id: customer.union_id || null,
          zip: customer.zip, country: customer.country, extra: customer.extra || null,
          metro_thana: customer.metro_thana || null, metro_thana_id: customer.metro_thana_id || null,
          neighbourhood: customer.neighbourhood || null, neighbourhood_id: customer.neighbourhood_id || null,
          ...profilePatch,
        }).eq('id', customerId)
      }

      const firstLine = validLines[0]
      const orderCode = 'ORD-' + new Date().getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000)
      const isMulti   = validLines.length > 1

      const { data: newOrder, error: orderError } = await supabase.from('orders').insert({
        seller_id: seller.id, customer_id: customerId, order_code: orderCode,
        // Keep single-product fields populated for backward compat
        product_id: firstLine.product_id, product_code: firstLine.product_code,
        product_name: firstLine.product_name, variant_id: firstLine.variant_id || null,
        variant_name: firstLine.variant_name || null,
        quantity: firstLine.quantity, unit_price: firstLine.unit_price,
        delivery_charge: order.delivery_charge, total_amount: grandTotal,
        items_subtotal: itemsSubtotal, has_multiple_items: isMulti,
        discount_percent: orderDiscountEnabled ? Number(orderDiscountPercent) : null,
        discount_amount: orderDiscAmt || null, discount_note: orderDiscountNote || null,
        vat_percent: orderTaxEnabled ? Number(orderTaxPercent) : null,
        vat_amount: orderTaxAmt || null, tax_type: orderTaxEnabled ? orderTaxType : null,
        tax_challan: orderTaxChallan || null,
        courier: order.courier === '__custom__' ? customCourier : order.courier,
        payment_method: order.payment_method,
        payment_status: order.payment_status, amount_paid: order.amount_paid || 0,
        order_source: order.order_source,
        submitted_by: parsedData ? 'Customer (paste format)' : 'Seller',
        notes: order.notes, taken_by: order.taken_by || null,
        tracking_number: order.tracking_number || null, tracking_url: order.tracking_url || null,
        expected_delivery: order.expected_delivery || null,
        event_id: order.event_id || null,
        event_name: events.find(e => e.id === order.event_id)?.name || null,
        delivery_person_name: order.delivery_person_name || null,
        delivery_person_phone: order.delivery_person_phone || null,
        status: isPreorder ? 'pre_order' : 'received',
        is_preorder: isPreorder || false,
      }).select().single()

      if (orderError) throw orderError

      // Insert order_items for ALL lines
      const itemRows = validLines.map(l => {
        const lc = calcLine(l)
        return {
          order_id: newOrder.id, seller_id: seller.id,
          product_id: l.product_id, product_code: l.product_code, product_name: l.product_name,
          variant_id: l.variant_id || null, variant_name: l.variant_name || null,
          quantity: Number(l.quantity), unit_price: Number(l.unit_price),
          line_discount_percent: l.line_discount_percent ? Number(l.line_discount_percent) : null,
          line_discount_amount: lc.discAmt || null,
          line_vat_percent: l.line_vat_percent ? Number(l.line_vat_percent) : null,
          line_vat_amount: lc.vatAmt || null,
          line_tax_type: l.line_vat_percent ? 'VAT' : null,
          line_subtotal: lc.subtotal,
          weight_kg: l.weight_kg || null,
        }
      })
      const { error: itemsError } = await supabase.from('order_items').insert(itemRows)
      if (itemsError) {
        console.error('order_items insert failed:', itemsError)
        showToast('Order saved but items list failed: ' + itemsError.message, 'error')
      }

      // Deduct stock per line — skip for pre-orders (stock deducts on fulfil)
      if (!isPreorder) for (const l of validLines) {
        if (l.variant_id) {
          const { data: v } = await supabase.from('product_variants').select('stock').eq('id', l.variant_id).single()
          if (v) await supabase.from('product_variants').update({ stock: Math.max(0, v.stock - Number(l.quantity)) }).eq('id', l.variant_id)
        } else {
          const { data: p } = await supabase.from('products').select('stock').eq('id', l.product_id).single()
          if (p) await supabase.from('products').update({ stock: Math.max(0, p.stock - Number(l.quantity)) }).eq('id', l.product_id)
        }
      }

      showToast(t("add_order.order_placed"))
      navigate('/orders/' + newOrder.id)
    } catch (e) {
      showToast(t("add_order.error_save") + " — " + e.message, 'error')
      setSaving(false)
    }
  }

  const couriers      = isInt ? INT_COURIERS : BD_COURIERS
  const paymentMethods = PAYMENT_BY_CURRENCY[currency] || PAYMENT_BY_CURRENCY.DEFAULT

  // ── RENDER ────────────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, overflowX: 'hidden', background: 'var(--bg-page)', fontFamily: 'var(--font-body)' }}>
      <Topbar title={t("add_order.title")} />
      <div className="ao-page">

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', padding: '14px 20px', marginBottom: '20px' }} className="ao-steps">
          <StepDot n={1} label={t("add_order.step_products")}           active={step===1} done={step>1} onClick={() => setStep(1)} />
          <div style={{ width: '32px', height: '2px', background: step > 1 ? 'var(--success)' : 'var(--border)', borderRadius: '2px', flexShrink: 0 }} />
          <StepDot n={2} label={t("add_order.step_customer")}           active={step===2} done={step>2} onClick={() => setStep(2)} />
          <div style={{ width: '32px', height: '2px', background: step > 2 ? 'var(--success)' : 'var(--border)', borderRadius: '2px', flexShrink: 0 }} />
          <StepDot n={3} label={t("add_order.step_delivery")} active={step===3} done={step>3} onClick={() => setStep(3)} />
        </div>

        {/* ── STEP 1 — PRODUCTS ──────────────────────────────────────── */}
        {step === 1 && (
          <>
            {/* Pre-order banner */}
            {isPreorder && (
              <div className="pc-fade-up" style={{ background: 'var(--success-light)', border: '1px solid var(--success-border)', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FlatIcon name="hourglass" size={20} color="#0D9488" />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0D9488', marginBottom: '2px', fontFamily: 'var(--font-ui)' }}>Pre-order Mode</div>
                  <div style={{ fontSize: '12px', color: '#0F766E' }}>This order will be tagged as a pre-order. Stock will NOT deduct now — only when you fulfil it from the Pre-orders tab.</div>
                </div>
              </div>
            )}
            {/* Quick tools */}
            <Card title={t("add_order.quick_tools")} subtitle={t("add_order.quick_tools_sub")}>
              <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', padding: '4px', borderRadius: '10px', background: 'var(--bg-muted)', width: 'fit-content', flexWrap: 'wrap' }}>
                {[{ key: 'copy', label: t("add_order.tab_copy") }, { key: 'share', label: t("add_order.tab_share") }, { key: 'paste', label: t("add_order.tab_paste") }].map(tab => (
                  <button key={tab.key} onClick={() => setQuickTab(tab.key)}
                    style={{ padding: '7px 14px', borderRadius: '7px', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: quickTab === tab.key ? 'var(--primary)' : 'transparent', color: quickTab === tab.key ? '#FFFFFF' : 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Flow guide */}
              <div className="pc-fade-up" style={{ display: 'flex', gap: '8px', marginBottom: '16px', padding: '10px 13px', borderRadius: '10px', background: 'var(--warning-light)', border: '1px solid var(--warning-border)' }}>
                <FlatIcon name="bulb" size={15} color="var(--warning)" style={{ marginTop: '1px' }} />
                <div style={{ fontSize: '11.5px', color: '#92400E', lineHeight: 1.7 }}>
                  <strong>{t("add_order.flow_guide")}</strong>
                  {' '}<span>① Select product below first</span>
                  {' → '}
                  <span>② Copy template or share form (product code auto-included)</span>
                  {' → '}
                  <span>③ When customer replies, paste here to auto-fill their details</span>
                </div>
              </div>

              {quickTab === 'copy' && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.6 }}>
                    Copy and send to your customer via Facebook inbox or WhatsApp.
                    {cart.some(l => l.product_id)
                      ? <span style={{ color: 'var(--success)', fontWeight: 600 }}> Product code is pre-filled in the template.</span>
                      : <span style={{ color: 'var(--warning)', fontWeight: 600 }}> Select a product below first — the product code will be included automatically.</span>}
                  </div>
                  <button onClick={handleCopyMessage}
                    style={{ padding: '10px 18px', borderRadius: '10px', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                    Copy message template
                  </button>
                </div>
              )}

              {quickTab === 'share' && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.6 }}>
                    Generate a one-time link — customer fills their info online. Link expires in 7 minutes.
                    {cart.some(l => l.product_id)
                      ? <span style={{ color: 'var(--success)', fontWeight: 600 }}> Product code will be pre-filled in the form.</span>
                      : <span style={{ color: 'var(--warning)', fontWeight: 600 }}> Select a product below first to include the product code in the form.</span>}
                  </div>
                  <button onClick={handleShareLink} disabled={generatingLink}
                    style={{ padding: '10px 18px', borderRadius: '10px', background: 'var(--text-primary)', border: 'none', fontSize: '13px', fontWeight: 600, color: '#FFFFFF', cursor: generatingLink ? 'not-allowed' : 'pointer', opacity: generatingLink ? 0.7 : 1, fontFamily: 'var(--font-ui)' }}>
                    {generatingLink ? t("add_order.share_generating") : 'Generate & copy link'}
                  </button>
                </div>
              )}

              {quickTab === 'paste' && (
                <div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.6 }}>
                    {t("add_order.paste_desc")} — name, phone, address and any profile fields auto-fill in Step 2. You add products in the cart below.
                  </div>
                  <div className="ao-paste-grid">
                    <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
                      placeholder={'Paste customer reply here...\n\nFull name: Hamza Ibn Alim\nPhone: 1684996934\nDistrict: Dhaka\n...'}
                      style={{ width: '100%', minHeight: '160px', border: '1px solid var(--border)', borderRadius: '9px', padding: '10px 12px', fontSize: '12px', outline: 'none', resize: 'vertical', fontFamily: 'var(--font-body)', color: 'var(--text-primary)', background: 'var(--bg-muted)', lineHeight: 1.7, boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                    <button onClick={handleParse} disabled={!pasteText.trim()} className="ao-parse-btn"
                      style={{ background: pasteText.trim() ? 'var(--primary)' : 'var(--border)', border: 'none', borderRadius: '8px', cursor: pasteText.trim() ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px' }}>
                      <FlatIcon name="arrowRight" size={14} color="#FFFFFF" />
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#FFFFFF', fontFamily: 'var(--font-ui)' }}>PARSE</span>
                    </button>
                    <div style={{ border: '1px solid var(--border)', borderRadius: '9px', minHeight: '120px', background: 'var(--bg-card)', padding: '10px 12px', fontSize: '12px' }}>
                      {!parsedData ? (
                        <span style={{ color: 'var(--text-muted)' }}>Parsed fields appear here</span>
                      ) : Object.keys(parsedData).length === 0 ? (
                        <span style={{ color: 'var(--error)' }}>No fields matched — check the format.</span>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 700, color: 'var(--success)', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>
                            <FlatIcon name="check" size={13} color="var(--success)" />{Object.keys(parsedData).length} fields extracted
                          </div>
                          {PASTE_FIELDS.map(f => parsedData[f.key] ? (
                            <div key={f.key} style={{ display: 'flex', gap: '6px', marginBottom: '3px' }}>
                              <span style={{ color: 'var(--text-muted)', fontSize: '11px', flexShrink: 0, width: '80px' }}>{f.label}</span>
                              <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '11.5px', wordBreak: 'break-word' }}>{parsedData[f.key]}</span>
                            </div>
                          ) : null)}
                        </>
                      )}
                    </div>
                  </div>
                  {parsedData && Object.keys(parsedData).length > 0 && (
                    <div style={{ marginTop: '10px', padding: '9px 13px', borderRadius: '9px', background: 'var(--primary-light)', fontSize: '12px', color: 'var(--primary)', fontWeight: 500 }}>
                      Customer details auto-filled in Step 2 — review before saving.
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Cart */}
            <Card title={t("add_order.step1_title")} subtitle={t("add_order.step1_sub")} accent>

              {/* Camera viewfinder */}
              {cameraOpen && (
                <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', background: 'var(--text-primary)', marginBottom: '12px' }}>
                  <video ref={videoRef} style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', display: 'block' }} playsInline muted />
                  {scanning && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: '160px', height: '80px', border: '2px solid var(--primary)', borderRadius: '8px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' }} />
                    </div>
                  )}
                  {cameraError && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '10px 14px', background: 'rgba(0,0,0,0.75)', color: '#FFFFFF', fontSize: '11.5px' }}>
                      {cameraError}
                    </div>
                  )}
                  <button onClick={stopCamera}
                    style={{ position: 'absolute', top: '8px', right: '8px', padding: '5px 12px', borderRadius: '8px', background: 'var(--error)', border: 'none', color: '#FFFFFF', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                    Stop camera
                  </button>
                </div>
              )}

              {/* Cart lines */}
              {cart.map((item, index) => (
                <CartItemRow key={index} item={item} index={index}
                  onUpdate={updateCartItem} onRemove={removeCartItem}
                  sym={sym} products={products} t={t} />
              ))}

              {/* Add product + scan buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <button onClick={addCartItem}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '2px dashed var(--primary)', background: 'var(--primary-light)', fontSize: '13px', fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                  + Add another product
                </button>
                <button onClick={() => openCamera(cart.length - 1)}
                  style={{ padding: '10px 16px', borderRadius: '10px', background: 'var(--text-primary)', border: 'none', fontSize: '12.5px', fontWeight: 600, color: '#FFFFFF', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'var(--font-ui)' }}>
                  <FlatIcon name="camera" size={14} color="#FFFFFF" />
                  Scan
                </button>
              </div>

              {/* Cart summary */}
              {itemsSubtotal > 0 && (
                <div style={{ background: 'var(--bg-muted)', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>Cart summary</div>
                  {cart.filter(l => l.product_id).map((l, i) => {
                    const lc = calcLine(l)
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '4px' }}>
                        <span style={{ color: 'var(--text-primary)' }}>{l.product_name || 'Product ' + (i + 1)}{l.product_color ? ' — ' + l.product_color : ''}{l.variant_name ? ' · ' + l.variant_name : ''} ×{l.quantity}</span>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{sym}{lc.subtotal.toLocaleString()}</span>
                      </div>
                    )
                  })}
                  <div style={{ borderTop: '1px solid var(--border)', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>Items subtotal</span>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--primary)', fontFamily: 'var(--font-ui)' }}>{sym}{itemsSubtotal.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <button onClick={() => setStep(2)}
                disabled={cart.filter(l => l.product_id).length === 0}
                style={{ width: '100%', padding: '13px', borderRadius: '10px', background: 'var(--primary)', border: 'none', fontSize: '13.5px', fontWeight: 700, color: '#FFFFFF', cursor: 'pointer', opacity: cart.filter(l => l.product_id).length === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'var(--font-ui)' }}>
                Next: Add customer details <FlatIcon name="arrowRight" size={14} color="#FFFFFF" />
              </button>
              {cart.filter(l => l.product_id).length === 0 && (
                <div style={{ textAlign: 'center', fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '8px' }}>Add at least one product to continue</div>
              )}
            </Card>
          </>
        )}

        {/* ── STEP 2 — CUSTOMER ──────────────────────────────────────── */}
        {step === 2 && (
          <Card title={t("add_order.step2_title")} subtitle={t("add_order.step2_sub")} accent>

            {/* Parsed fields summary — collapsible */}
            {parsedData && Object.keys(parsedData).length > 0 && (
              <ParsedSummary parsedData={parsedData} />
            )}

            {/* Phone lookup */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px', fontFamily: 'var(--font-ui)' }}>Look up returning customer by phone</div>
              <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.6 }}>
                Enter the customer's phone and hit Lookup. If they've ordered before, their details — including profile info — auto-fill.
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }} className="ao-lookup-row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PhoneInput label="" countryCode={searchCountryCode} phone={searchPhone}
                    onCountryCodeChange={setSearchCountryCode} onPhoneChange={setSearchPhone}
                    placeholder={isInt ? '412345678' : '1712345678'} />
                </div>
                <button onClick={lookupPhone} disabled={lookingUp}
                  style={{ padding: '9px 16px', borderRadius: '9px', background: 'var(--text-primary)', border: 'none', fontSize: '12.5px', fontWeight: 600, color: '#FFFFFF', cursor: lookingUp ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: lookingUp ? 0.7 : 1, fontFamily: 'var(--font-ui)' }}>
                  {lookingUp ? t("add_order.lookup_looking") : t("add_order.lookup_btn")}
                </button>
              </div>
              {existingCustomer && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '9px', background: 'var(--success-light)', border: '1px solid var(--success-border)', fontSize: '12px', color: '#065F46', fontWeight: 500 }}>
                  <FlatIcon name="check" size={13} color="var(--success)" />
                  {t("add_order.returning_customer")} — {existingCustomer.name}. {t("add_order.address_autofilled")}
                </div>
              )}

              {multiMatches.length > 0 && (
                <div style={{ marginTop: '10px', padding: '12px 14px', borderRadius: '10px', background: 'var(--warning-light)', border: '1px solid var(--warning-border)' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#92400E', marginBottom: '8px', fontFamily: 'var(--font-ui)' }}>
                    {multiMatches.length} customers found with this number — who is this order for?
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                    {multiMatches.map(m => (
                      <button key={m.id} onClick={() => applyFoundCustomer(m)}
                        style={{ textAlign: 'left', padding: '9px 12px', borderRadius: '9px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer' }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{m.name || 'Unnamed'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{[m.district, m.division].filter(Boolean).join(', ') || 'No address on file'}</div>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => startNewCustomer(normalizePhone(searchCountryCode, searchPhone))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: '9px', border: '1px dashed var(--border)', background: 'transparent', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                    None of these — this is a new customer
                  </button>
                </div>
              )}

              {flagCheck && (flagCheck.privateFlag || flagCheck.communityFlag) && (() => {
                const cf  = flagCheck.communityFlag
                const pf  = flagCheck.privateFlag
                const cfg = cf ? FLAG_LEVEL_CONFIG[cf.level] || FLAG_LEVEL_CONFIG.watch : FLAG_LEVEL_CONFIG.watch
                return (
                  <div style={{ marginTop: '10px', padding: '12px 14px', borderRadius: '10px', background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: cfg.color, marginBottom: '4px', fontFamily: 'var(--font-ui)' }}>
                      <FlatIcon name="warning" size={14} color={cfg.color} /> Customer Flagged — {cfg.label}
                    </div>
                    {cf && (
                      <div style={{ fontSize: '12px', color: cfg.color, marginBottom: '3px' }}>
                        Reported by {cf.flag_count} seller{cf.flag_count !== 1 ? 's' : ''} for: <strong>{cf.dominant_reason?.replace(/_/g, ' ')}</strong>
                      </div>
                    )}
                    {pf && !cf && (
                      <div style={{ fontSize: '12px', color: cfg.color, marginBottom: '3px' }}>
                        You previously flagged this customer for: <strong>{pf.reason_category?.replace(/_/g, ' ')}</strong>
                      </div>
                    )}
                    <div style={{ fontSize: '11.5px', color: cfg.color, marginTop: '4px', lineHeight: 1.5 }}>
                      You can still proceed — this is a warning only. Consider requesting advance payment.
                    </div>
                  </div>
                )
              })()}
            </div>

            <div style={{ height: '1px', background: 'var(--border)', margin: '0 0 16px' }} />

            <div style={{ marginBottom: '12px' }}>
              <Field label={t("add_order.full_name")}>
                <Inp value={customer.name} onChange={v => setC('name', v)} placeholder="Customer's full name" />
              </Field>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <PhoneInput label={t("add_order.phone_number")} countryCode={customer.country_code} phone={customer.phone}
                onCountryCodeChange={v => setC('country_code', v)} onPhoneChange={v => setC('phone', v)}
                placeholder={isInt ? '412345678' : '1712345678'} required />
            </div>

            <div style={{ height: '1px', background: 'var(--border)', margin: '0 0 16px' }} />

            <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', fontFamily: 'var(--font-ui)' }}>Delivery address</div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '14px', lineHeight: 1.6 }}>
              {t("add_order.address_desc")}
            </div>
            <BDAddressFields address={customer} onChange={setC} isBD={!isInt} />

            <div style={{ height: '1px', background: 'var(--border)', margin: '16px 0' }} />

            {/* ── Customer profile (optional) ─────────────────────────── */}
            <CollapsibleSection
              title={t("add_order.profile_title")}
              hint={t("add_order.profile_hint")}
              open={showProfileSection}
              onToggle={() => setShowProfileSection(o => !o)}
              badge={filledProfileFields.length === 0 ? null : (
                <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: profileComplete ? 'var(--success-light)' : 'var(--warning-light)', color: profileComplete ? 'var(--success)' : 'var(--warning)', fontFamily: 'var(--font-ui)' }}>
                  {profileComplete ? t("add_order.profile_complete") : `${filledProfileFields.length}/8`}
                </span>
              )}>
              <div className="ao-grid-2">
                <Field label={t("add_order.profile_dob")} optional>
                  <Inp type="date" value={customer.birthday} onChange={v => setC('birthday', v)} />
                </Field>
                <Field label={t("add_order.profile_anniversary")} optional>
                  <Inp type="date" value={customer.anniversary} onChange={v => setC('anniversary', v)} />
                </Field>
              </div>
              <div className="ao-grid-2">
                <Field label={t("add_order.profile_gender")} optional>
                  <Sel value={customer.gender} onChange={v => setC('gender', v)}>
                    {GENDER_OPTIONS.map(g => <option key={g} value={g === 'Not specified' ? '' : g}>{g}</option>)}
                  </Sel>
                </Field>
                <Field label={t("add_order.profile_age_group")} optional>
                  <Sel value={customer.age_range} onChange={v => setC('age_range', v)}>
                    {AGE_GROUPS.map(a => <option key={a} value={a === 'Not specified' ? '' : a}>{a}</option>)}
                  </Sel>
                </Field>
              </div>
              <div className="ao-grid-2">
                <Field label={t("add_order.profile_whatsapp")} optional>
                  <Inp value={customer.whatsapp} onChange={v => setC('whatsapp', v)} placeholder={t("add_order.profile_whatsapp_placeholder")} />
                </Field>
                <Field label={t("add_order.profile_email")} optional>
                  <Inp type="email" value={customer.email} onChange={v => setC('email', v)} placeholder="email@example.com" />
                </Field>
              </div>
              <div className="ao-grid-2">
                <Field label={t("add_order.profile_facebook")} optional>
                  <Inp value={customer.facebook_url} onChange={v => setC('facebook_url', v)} placeholder="facebook.com/..." />
                </Field>
                <Field label={t("add_order.profile_instagram")} optional>
                  <Inp value={customer.instagram_url} onChange={v => setC('instagram_url', v)} placeholder="instagram.com/..." />
                </Field>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                <FlatIcon name="gift" size={12} color="var(--text-muted)" style={{ marginTop: '1px' }} />
                <span>{t("add_order.profile_footer_note")}</span>
              </div>
            </CollapsibleSection>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setStep(1)}
                style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'var(--font-ui)' }}>
                <FlatIcon name="arrowLeft" size={13} color="var(--text-muted)" /> Back
              </button>
              <button onClick={() => setStep(3)} disabled={!customer.name || !customer.phone}
                style={{ flex: 2, padding: '12px', borderRadius: '10px', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#FFFFFF', cursor: 'pointer', opacity: (!customer.name || !customer.phone) ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'var(--font-ui)' }}>
                Next: Delivery &amp; Payment <FlatIcon name="arrowRight" size={13} color="#FFFFFF" />
              </button>
            </div>
          </Card>
        )}

        {/* ── STEP 3 — DELIVERY & PAYMENT ──────────────────────────────── */}
        {step === 3 && (
          <>
            <Card title={t("add_order.step3_title")} subtitle={t("add_order.step3_sub")} accent>
              <div className="ao-grid-2">
                <Field label={t("add_order.delivery_charge") + ` (${sym})`}>
                  <Inp type="number" value={order.delivery_charge} onChange={v => setO('delivery_charge', v)} placeholder="0" />
                </Field>
                <Field label={t("add_order.courier")}>
                  <Sel value={order.courier} onChange={v => { setO('courier', v); setShowManualDelivery(v === 'Manual delivery') }}>
                    {couriers.map(c => <option key={c}>{c}</option>)}
                    <option value="__custom__">+ Other</option>
                  </Sel>
                </Field>
              </div>
              {order.courier === '__custom__' && (
                <div style={{ marginTop: '12px' }}>
                  <Field label={t("add_order.custom_courier")}><Inp value={customCourier} onChange={setCustomCourier} placeholder="Type courier name..." /></Field>
                </div>
              )}
              {(showManualDelivery || order.courier === 'Manual delivery') && (
                <div style={{ marginTop: '12px', padding: '13px', borderRadius: '10px', background: 'var(--warning-light)', border: '1px solid var(--warning-border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#92400E', marginBottom: '10px', fontFamily: 'var(--font-ui)' }}>Delivery person details</div>
                  <div className="ao-grid-2">
                    <Field label="Name" optional><Inp value={order.delivery_person_name} onChange={v => setO('delivery_person_name', v)} placeholder="e.g. Karim" /></Field>
                    <Field label="Phone" optional><Inp value={order.delivery_person_phone} onChange={v => setO('delivery_person_phone', v)} placeholder="017..." /></Field>
                  </div>
                </div>
              )}
              <div className="ao-grid-2" style={{ marginTop: '12px' }}>
                <Field label={isPreorder ? 'Expected Delivery / Fulfilment Date *' : t("add_order.expected_delivery")} optional={!isPreorder}>
                  <Inp type="date" value={order.expected_delivery} onChange={v => setO('expected_delivery', v)} style={isPreorder && !order.expected_delivery ? { borderColor: '#0D9488' } : {}} />
                </Field>
                <Field label={t("add_order.tracking_number")} optional><Inp value={order.tracking_number} onChange={v => setO('tracking_number', v)} placeholder="e.g. PT-123456" /></Field>
              </div>
              <div style={{ marginTop: '12px' }}>
                <Field label={t("add_order.tracking_link")} optional><Inp value={order.tracking_url} onChange={v => setO('tracking_url', v)} placeholder={t("add_order.tracking_placeholder")} /></Field>
              </div>
              {events.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Field label={t("add_order.link_event")} optional>
                    <Sel value={order.event_id} onChange={v => setO('event_id', v)}>
                      <option value="">No event — regular order</option>
                      {events.map(e => <option key={e.id} value={e.id}>{e.name} · {new Date(e.start_date).toLocaleDateString('en-BD', { day: 'numeric', month: 'short' })}</option>)}
                    </Sel>
                  </Field>
                </div>
              )}
            </Card>

            <Card title={t("add_order.payment_title")} subtitle={t("add_order.payment_sub")}>
              <div className="ao-grid-2">
                <Field label={t("add_order.payment_method")}>
                  <Sel value={order.payment_method} onChange={v => setO('payment_method', v)}>
                    {paymentMethods.map(m => <option key={m}>{m}</option>)}
                  </Sel>
                </Field>
                <Field label={t("add_order.payment_status")}>
                  <Sel value={order.payment_status} onChange={v => setO('payment_status', v)}>
                    {PAYMENT_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </Sel>
                </Field>
                <Field label={t("add_order.amount_paid") + ` (${sym})`}>
                  <Inp type="number" value={order.amount_paid} onChange={v => setO('amount_paid', v)} placeholder="0" />
                </Field>
                <Field label={t("add_order.order_source")}>
                  <Sel value={order.order_source} onChange={v => setO('order_source', v)}>
                    {ORDER_SOURCES.map(s => <option key={s}>{s}</option>)}
                  </Sel>
                </Field>
                <Field label={t("add_order.taken_by")} optional>
                  <Inp value={order.taken_by} onChange={v => setO('taken_by', v)} placeholder={t("add_order.taken_by_placeholder")} />
                </Field>
              </div>

              {/* Order-level discount */}
              <div style={{ marginTop: '16px' }}>
                <Toggle enabled={orderDiscountEnabled} onToggle={() => setOrderDiscountEnabled(!orderDiscountEnabled)}
                  label={t("add_order.order_discount")}
                  summary={orderDiscountEnabled && orderDiscountPercent ? `-${orderDiscountPercent}% = -${sym}${orderDiscAmt.toLocaleString()}` : null} />
                {orderDiscountEnabled && (
                  <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 10px 10px', marginTop: '-12px', marginBottom: '12px' }}>
                    <div className="ao-grid-2">
                      <Field label={t("add_order.discount_pct")}><Inp type="number" value={orderDiscountPercent} onChange={setOrderDiscountPercent} placeholder="e.g. 10" /></Field>
                      <Field label={t("add_order.discount_reason")} optional><Inp value={orderDiscountNote} onChange={setOrderDiscountNote} placeholder={t("add_order.discount_reason_placeholder")} /></Field>
                    </div>
                  </div>
                )}

                {/* Order-level tax */}
                <Toggle enabled={orderTaxEnabled} onToggle={() => setOrderTaxEnabled(!orderTaxEnabled)}
                  label={t("add_order.order_vat")}
                  summary={orderTaxEnabled && orderTaxPercent ? `+${orderTaxPercent}% = +${sym}${orderTaxAmt.toLocaleString()}` : null} />
                {orderTaxEnabled && (
                  <div style={{ padding: '12px 14px', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 10px 10px', marginTop: '-12px', marginBottom: '12px' }}>
                    <div style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--warning-light)', border: '1px solid var(--warning-border)', fontSize: '11.5px', color: '#92400E', marginBottom: '12px' }}>
                      {t("add_order.tax_advice")}
                    </div>
                    <div className="ao-grid-2">
                      <Field label={t("add_order.tax_type")}>
                        <Sel value={orderTaxType} onChange={setOrderTaxType}>
                          {['VAT','GST','Sales Tax','Service Tax','Other'].map(taxType => <option key={taxType}>{taxType}</option>)}
                        </Sel>
                      </Field>
                      <Field label={t("add_order.tax_pct")}><Inp type="number" value={orderTaxPercent} onChange={setOrderTaxPercent} placeholder="e.g. 15" /></Field>
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <Field label={t("add_order.tax_challan")} optional><Inp value={orderTaxChallan} onChange={setOrderTaxChallan} placeholder={t("add_order.tax_challan_placeholder")} /></Field>
                    </div>
                  </div>
                )}
              </div>

              <Field label={t("add_order.notes")} optional>
                <textarea value={order.notes} onChange={e => setO('notes', e.target.value)}
                  placeholder={t("add_order.notes_placeholder")}
                  rows={2}
                  style={{ width: '100%', border: '1px solid var(--border)', borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px', outline: 'none', resize: 'none', fontFamily: 'var(--font-body)', color: 'var(--text-primary)', background: 'var(--bg-card)', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </Field>
            </Card>

            {/* Grand total summary */}
            <Card title={t("add_order.order_summary")}>
              {cart.filter(l => l.product_id).map((l, i) => {
                const lc = calcLine(l)
                return (
                  <div key={i} style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: i < cart.filter(ll => ll.product_id).length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', fontWeight: 600, marginBottom: '3px' }}>
                      <span style={{ color: 'var(--text-primary)' }}>{l.product_name}{l.product_color ? ' — ' + l.product_color : ''}{l.variant_name ? ' · ' + l.variant_name : ''}</span>
                      <span style={{ color: 'var(--text-primary)' }}>{sym}{lc.subtotal.toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>
                      {l.quantity} × {sym}{Number(l.unit_price).toLocaleString()}
                      {lc.discAmt > 0 && <span style={{ color: 'var(--error)' }}> − {sym}{lc.discAmt.toLocaleString()} disc</span>}
                      {lc.vatAmt > 0 && <span> + {sym}{lc.vatAmt.toLocaleString()} VAT</span>}
                    </div>
                  </div>
                )
              })}

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Items subtotal</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{sym}{itemsSubtotal.toLocaleString()}</span>
                </div>
                {orderDiscAmt > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Order discount ({orderDiscountPercent}%)</span>
                    <span style={{ color: 'var(--error)', fontWeight: 600 }}>&#8722;{sym}{orderDiscAmt.toLocaleString()}</span>
                  </div>
                )}
                {orderTaxAmt > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>{orderTaxType} ({orderTaxPercent}%)</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>+{sym}{orderTaxAmt.toLocaleString()}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{t("add_order.delivery")}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{sym}{Number(order.delivery_charge || 0).toLocaleString()}</span>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>{t("add_order.grand_total")}</span>
                  <span style={{ fontSize: '22px', fontWeight: 700, color: 'var(--primary)', fontFamily: 'var(--font-ui)' }}>{sym}{grandTotal.toLocaleString()}</span>
                </div>
                {Number(order.amount_paid) > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
                      <span style={{ color: 'var(--text-muted)' }}>Amount paid</span>
                      <span style={{ color: 'var(--success)', fontWeight: 600 }}>&#8722;{sym}{Number(order.amount_paid).toLocaleString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 700 }}>
                      <span style={{ color: 'var(--text-primary)' }}>{t("add_order.amount_due")}</span>
                      <span style={{ color: amountDue > 0 ? 'var(--error)' : 'var(--success)' }}>{sym}{Math.max(0, amountDue).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>

              {parsedData && (
                <div style={{ marginTop: '12px', padding: '8px 12px', borderRadius: '9px', background: 'var(--primary-light)', fontSize: '12px', color: 'var(--primary)' }}>
                  This order will be tagged as <strong>Customer (paste format)</strong>.
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button onClick={() => setStep(2)}
                  style={{ flex: 1, padding: '13px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'var(--font-ui)' }}>
                  <FlatIcon name="arrowLeft" size={13} color="var(--text-muted)" /> Back
                </button>
                <button onClick={handleSubmit} disabled={saving}
                  style={{ flex: 2, padding: '13px', borderRadius: '10px', background: saving ? 'var(--border)' : 'var(--primary)', border: 'none', fontSize: '14px', fontWeight: 700, color: '#FFFFFF', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'var(--font-ui)' }}>
                  {isPreorder && !saving && <FlatIcon name="hourglass" size={14} color="#FFFFFF" />}
                  {saving ? (isPreorder ? 'Placing Pre-order...' : t("add_order.placing_order")) : (isPreorder ? 'Place Pre-order' : t("add_order.place_order"))}
                </button>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* ── Responsive styles ─────────────────────────────────────────── */}
      <style>{`
        @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; }
        .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .pc-card-hover:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }

        .ao-page { padding: 18px 24px; }
        @media (max-width: 768px) {
          .ao-page { padding: 14px 16px; }
        }

        .ao-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 10px; }
        @media (max-width: 600px) {
          .ao-grid-2 { grid-template-columns: 1fr; gap: 10px; }
        }

        @media (max-width: 480px) {
          .ao-step-label { display: none; }
        }

        .ao-paste-grid { display: grid; grid-template-columns: 1fr 56px 1fr; gap: 8px; align-items: stretch; }
        .ao-parse-btn { flex-direction: column; }
        @media (max-width: 640px) {
          .ao-paste-grid { grid-template-columns: 1fr; }
          .ao-parse-btn { flex-direction: row; }
        }

        .ao-lookup-row { }
        @media (max-width: 480px) {
          .ao-lookup-row { flex-direction: column; }
          .ao-lookup-row > button { width: 100%; }
        }
      `}</style>
    </div>
  )
}

export default AddOrder